import { useState, useEffect, useRef, useMemo } from 'react';
import { BACKEND_URL } from '@/lib/env';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { VIBE_CHAT_API_BASE } from '@/lib/vibeChatConfig';
import PrdArtifactCard from '@/components/PrdArtifactCard';
import { displayChatMessageContent, isHiddenChatMessage } from '@/lib/chatDisplay';
import { useChatPrdAvailable, shouldShowPrdArtifact } from '@/hooks/useChatPrdAvailable';
import {
  Code,
  ExternalLink,
  MessageSquarePlus,
  Send,
  Loader2,
  Copy,
  Check,
  Activity,
  Brain,
  Sparkles,
  Code2,
  AlertCircle,
  CheckCircle2,
  MessageSquare,
  History,
} from 'lucide-react';

const BACKEND_URL_CLEAN = (BACKEND_URL || '').replace(/\/$/, '');
const API = BACKEND_URL_CLEAN ? `${BACKEND_URL_CLEAN}/api` : '';

function extractCodeFromBackticks(content) {
  if (!content) return content;
  const codeBlockRegex = /```[\s\S]*?```/g;
  const matches = content.match(codeBlockRegex);
  if (matches && matches.length > 0) {
    return matches
      .map((match) => match.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, ''))
      .join('\n\n');
  }
  return content;
}

function formatApiError(err) {
  const d = err?.response?.data;
  if (d == null) return err?.message || 'Request failed';
  if (typeof d.detail === 'string') return d.detail;
  if (Array.isArray(d.detail)) {
    return d.detail.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join('; ');
  }
  if (typeof d === 'object') return JSON.stringify(d);
  return String(d);
}

function mapChatResponse(data) {
  return {
    sessionId: data.session_id,
    awaitingUser: Boolean(data.awaiting_user),
    phase: data.phase ?? '',
    message: data.message ?? '',
    generatedCode: data.generated_code ?? null,
    prdAvailable: Boolean(data.prd_available),
    generating: Boolean(data.generating),
  };
}

/** Match ticket page “typing” affordance */
function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-1 py-0.5" aria-hidden>
      <span
        className="h-2 w-2 rounded-full bg-purple-500 opacity-70"
        style={{ animation: 'vibeTypingDot 1.4s infinite ease-in-out', animationDelay: '0ms' }}
      />
      <span
        className="h-2 w-2 rounded-full bg-purple-500 opacity-70"
        style={{ animation: 'vibeTypingDot 1.4s infinite ease-in-out', animationDelay: '200ms' }}
      />
      <span
        className="h-2 w-2 rounded-full bg-purple-500 opacity-70"
        style={{ animation: 'vibeTypingDot 1.4s infinite ease-in-out', animationDelay: '400ms' }}
      />
      <style>{`
        @keyframes vibeTypingDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function usePhaseVisual(phase, chatFinished, chatAwaitingUser, hasSession) {
  return useMemo(() => {
    const p = (phase || '').toUpperCase();
    if (chatFinished || p === 'DONE') {
      return {
        progress: 100,
        headline: 'Generation complete',
        sub: 'ABAP is ready below — start a new chat for another program.',
        badgeClass: 'border-emerald-300 bg-emerald-50 text-emerald-900 font-medium',
        ring: 'from-emerald-500 to-teal-600',
      };
    }
    if (p === 'CONFIRM') {
      return {
        progress: 72,
        headline: 'Confirmation',
        sub: 'Review the PRD on the right, then reply yes, approve, or ok to generate ABAP.',
        badgeClass: 'border-amber-300 bg-amber-50 text-amber-900 font-medium',
        ring: 'from-amber-500 to-orange-500',
      };
    }
    if (p === 'GENERATE') {
      return {
        progress: 88,
        headline: 'Generating ABAP',
        sub: 'Running in the background — usually 1–3 minutes. Stay on this page.',
        badgeClass: 'border-indigo-300 bg-indigo-50 text-indigo-900 font-medium',
        ring: 'from-indigo-500 to-violet-600',
      };
    }
    if (p === 'GATHER') {
      return {
        progress: 38,
        headline: 'Gathering requirements',
        sub: 'Answer each question so we can shape your report or program.',
        badgeClass: 'border-violet-300 bg-violet-50 text-violet-900 font-medium',
        ring: 'from-violet-500 to-indigo-600',
      };
    }
    if (hasSession && chatAwaitingUser) {
      return {
        progress: 55,
        headline: 'Your turn',
        sub: 'The assistant is waiting for your message.',
        badgeClass: 'border-blue-300 bg-blue-50 text-blue-900 font-medium',
        ring: 'from-blue-500 to-indigo-600',
      };
    }
    if (hasSession) {
      return {
        progress: 20,
        headline: 'In progress',
        sub: 'Follow the assistant message and reply when prompted.',
        badgeClass: 'border-purple-200 bg-purple-50 text-purple-900 font-medium',
        ring: 'from-purple-500 to-indigo-600',
      };
    }
    return {
      progress: 0,
      headline: 'No active session',
      sub: 'Start a chat to collect requirements in natural language.',
      badgeClass: 'border-slate-200 bg-slate-50 text-slate-700 font-medium',
      ring: 'from-slate-400 to-slate-600',
    };
  }, [phase, chatFinished, chatAwaitingUser, hasSession]);
}

function ChatMessageRow({ role, content }) {
  const isUser = role === 'user';
  return (
    <div
      className={cn(
        'flex min-w-0 gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300',
        isUser && 'flex-row-reverse'
      )}
    >
      <div
        className={cn(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white shadow-md ring-2 ring-offset-2 ring-offset-background',
          isUser
            ? 'bg-gradient-to-br from-blue-500 to-indigo-600 ring-blue-200'
            : 'bg-gradient-to-br from-purple-500 to-indigo-600 ring-purple-200'
        )}
        aria-hidden
      >
        {isUser ? 'You' : <Brain className="h-4 w-4" />}
      </div>
      <div
        className={cn(
          'min-w-0 flex-1 rounded-xl border p-3.5 shadow-sm transition-shadow hover:shadow-md',
          isUser
            ? 'border-blue-200/80 bg-gradient-to-br from-blue-50/90 to-indigo-50/50'
            : 'border-purple-100/90 bg-white/95 backdrop-blur-sm'
        )}
      >
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span
            className={cn(
              'text-[11px] font-semibold uppercase tracking-wide',
              isUser ? 'text-blue-800' : 'text-purple-800'
            )}
          >
            {isUser ? 'You' : 'Assistant'}
          </span>
        </div>
        <div
          className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700"
          style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
        >
          {content}
        </div>
      </div>
    </div>
  );
}

export default function VibeCodingStudio() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [codeGenerations, setCodeGenerations] = useState({});
  const [loading, setLoading] = useState(true);

  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatAwaitingUser, setChatAwaitingUser] = useState(false);
  const [chatPhase, setChatPhase] = useState('');
  const [chatGeneratedCode, setChatGeneratedCode] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatStarting, setChatStarting] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [chatGenerating, setChatGenerating] = useState(false);
  const [prdAvailableHint, setPrdAvailableHint] = useState(false);
  const [copied, setCopied] = useState(false);
  const chatEndRef = useRef(null);

  const { prdAvailable, prdChecking, markPrdAvailable } = useChatPrdAvailable(
    chatSessionId,
    prdAvailableHint
  );

  const chatFinished = !chatAwaitingUser && chatPhase === 'DONE';
  const chatGeneratingActive =
    chatGenerating || String(chatPhase).toUpperCase() === 'GENERATE';
  const canSendChat =
    Boolean(chatSessionId) &&
    !chatFinished &&
    !chatGeneratingActive &&
    !chatStarting &&
    !chatSending &&
    chatInput.trim().length > 0;

  const phaseVisual = usePhaseVisual(chatPhase, chatFinished, chatAwaitingUser, Boolean(chatSessionId));

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatSending]);

  useEffect(() => {
    if (!VIBE_CHAT_API_BASE || !chatSessionId || !chatGeneratingActive) {
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${VIBE_CHAT_API_BASE}/chat/${chatSessionId}/status`);
        const m = mapChatResponse(data);
        if (cancelled) return;
        applyPrdFlag(m);
        setChatPhase(m.phase);
        setChatAwaitingUser(m.awaitingUser);
        if (m.generatedCode) {
          setChatGeneratedCode(m.generatedCode);
        }
        if (!m.generating) {
          setChatGenerating(false);
          setChatAwaitingUser(m.awaitingUser);
          setChatPhase(m.phase);
          if (m.generatedCode) {
            setChatGeneratedCode(m.generatedCode);
          }
          if (String(m.phase).toUpperCase() === 'ERROR') {
            setChatError(m.message || 'Request failed');
          } else if (m.message) {
            const display = displayChatMessageContent(m.message, {
              role: 'assistant',
              generatedCode: m.generatedCode,
              phase: m.phase,
            });
            setChatMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant' && last?.content === display) {
                return prev;
              }
              return [...prev, { role: 'assistant', content: display }];
            });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setChatError(formatApiError(err));
        }
      }
    };
    poll();
    const timer = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [chatSessionId, chatGeneratingActive]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    if (!API) {
      setLoading(false);
      return;
    }
    try {
      const ticketsRes = await axios.get(`${API}/tickets`);
      const ticketsWithDev = ticketsRes.data.filter((t) =>
        ['in_development', 'in_testing', 'in_deployment', 'in_governance', 'completed'].includes(t.status)
      );
      setTickets(ticketsWithDev);
      const codeData = {};
      for (const ticket of ticketsWithDev) {
        try {
          const codeRes = await axios.get(`${API}/code/${ticket.ticket_number}`);
          if (codeRes.data.length > 0) {
            codeData[ticket.id] = codeRes.data;
          }
        } catch {
          /* no code */
        }
      }
      setCodeGenerations(codeData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startChatSession = async () => {
    if (!VIBE_CHAT_API_BASE) return;
    setChatError(null);
    setChatStarting(true);
    setChatSessionId(null);
    setChatAwaitingUser(false);
    setChatPhase('');
    setChatGeneratedCode(null);
    setChatMessages([]);
    setChatInput('');
    setPrdAvailableHint(false);
    try {
      const { data } = await axios.post(`${VIBE_CHAT_API_BASE}/chat/start`, {});
      const m = mapChatResponse(data);
      applyPrdFlag(m);
      setChatSessionId(m.sessionId);
      setChatAwaitingUser(m.awaitingUser);
      setChatPhase(m.phase);
      setChatGeneratedCode(m.generatedCode);
      setChatGenerating(Boolean(m.generating));
      if (m.message) {
        setChatMessages([{ role: 'assistant', content: m.message }]);
      } else {
        setChatMessages([]);
      }
    } catch (err) {
      setChatError(formatApiError(err));
    } finally {
      setChatStarting(false);
    }
  };

  const sendChatMessage = async () => {
    const text = chatInput.trim();
    if (!VIBE_CHAT_API_BASE || !chatSessionId || !text || chatFinished) return;
    setChatError(null);
    setChatSending(true);
    setChatInput('');
    setChatMessages((prev) => [...prev, { role: 'user', content: text }]);
    try {
      const { data } = await axios.post(`${VIBE_CHAT_API_BASE}/chat/${chatSessionId}/message`, { text });
      const m = mapChatResponse(data);
      applyPrdFlag(m);
      setChatAwaitingUser(m.awaitingUser);
      setChatPhase(m.phase);
      setChatGeneratedCode(m.generatedCode);
      setChatGenerating(Boolean(m.generating));
      if (m.message) {
        const display = displayChatMessageContent(m.message, {
          role: 'assistant',
          generatedCode: m.generatedCode,
          phase: m.phase,
        });
        setChatMessages((prev) => [...prev, { role: 'assistant', content: display }]);
      }
    } catch (err) {
      setChatError(formatApiError(err));
    } finally {
      setChatSending(false);
    }
  };

  const copyGeneratedCode = async () => {
    const code = chatGeneratedCode || '';
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setChatError('Could not copy to clipboard');
    }
  };

  const showAbapPanel = (chatGeneratedCode != null && chatGeneratedCode !== '') || chatFinished;
  const showPrdArtifact = Boolean(chatSessionId) && shouldShowPrdArtifact(chatPhase);

  const applyPrdFlag = (m) => {
    if (m?.prdAvailable) {
      setPrdAvailableHint(true);
      markPrdAvailable();
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50/80 via-white to-indigo-50/60 p-12 shadow-sm">
        <div className="rounded-full bg-purple-100 p-4 ring-4 ring-purple-200/50">
          <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
        </div>
        <p className="text-sm font-medium text-purple-900">Loading Vibe Coding Studio…</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-10" data-testid="vibe-coding-studio">
      {/* Hero — ticket-detail adjacent language */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 p-6 shadow-sm md:p-8">
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br from-purple-200/40 to-indigo-200/30 blur-2xl"
          aria-hidden
        />
        <div className="relative flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex min-w-0 gap-4">
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-md ring-2 ring-purple-100">
              <Sparkles className="h-7 w-7 text-purple-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">Vibe Coding Studio</h1>
              <p className="mt-2 max-w-2xl text-muted-foreground">
                Describe what you need in natural language. We will gather requirements, confirm a product brief,
                and generate ABAP code for your SAP report or program.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-stretch gap-3 sm:items-end">
            <Button
              variant="outline"
              size="sm"
              className="border-indigo-200 bg-white/90 shadow-sm hover:bg-indigo-50"
              asChild
            >
              <Link to="/vibe-studio/chat" data-testid="link-chat-history">
                <History className="mr-2 h-4 w-4 text-indigo-600" />
                Chat history
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {!VIBE_CHAT_API_BASE && (
        <Alert variant="destructive" className="border-red-200 shadow-sm">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Chat unavailable</AlertTitle>
          <AlertDescription>
            ABAP chat is not configured for this environment. Contact your administrator if this persists.
          </AlertDescription>
        </Alert>
      )}
      {!BACKEND_URL && (
        <Alert className="border-amber-200 bg-amber-50/50 shadow-sm">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertTitle>Ticket code tab</AlertTitle>
          <AlertDescription>
            Ticket code is unavailable in this environment. You can still use the ABAP chat above.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="chat-abap" className="w-full">
        <TabsList className="h-11 w-full max-w-xl justify-start gap-1 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-100/90 to-slate-50 p-1 shadow-sm sm:w-auto">
          <TabsTrigger
            value="chat-abap"
            data-testid="tab-chat-abap"
            className="rounded-lg px-4 data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm"
          >
            <MessageSquare className="mr-2 h-4 w-4 opacity-70" />
            Chat ABAP
          </TabsTrigger>
          <TabsTrigger
            value="tickets"
            data-testid="tab-ticket-code"
            className="rounded-lg px-4 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
          >
            <Code2 className="mr-2 h-4 w-4 opacity-70" />
            Ticket code
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chat-abap" className="mt-6 focus-visible:outline-none">
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-12 xl:items-stretch">
            {/* Main stream */}
            <Card className="flex min-h-[520px] flex-col overflow-hidden border-slate-200/90 shadow-md xl:col-span-7">
              <div className="border-b border-purple-100 bg-gradient-to-r from-purple-50 via-indigo-50/80 to-blue-50 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <div className="rounded-lg bg-white/90 p-1.5 shadow-sm ring-1 ring-purple-100">
                      <Activity className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="truncate font-semibold text-slate-900">Requirements conversation</h2>
                      <p className="text-xs text-purple-800/80">
                        Tell us what you want to build. We will ask clarifying questions, prepare a requirements
                        document, and generate ABAP when you approve.
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={startChatSession}
                    disabled={!VIBE_CHAT_API_BASE || chatStarting || chatSending}
                    className="shrink-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md hover:from-purple-700 hover:to-indigo-700"
                    data-testid="chat-new-session"
                  >
                    {chatStarting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Starting…
                      </>
                    ) : (
                      <>
                        <MessageSquarePlus className="mr-2 h-4 w-4" />
                        New chat
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <CardHeader className="space-y-3 border-b border-slate-100 bg-slate-50/40 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  {chatPhase && (
                    <Badge className={cn('text-xs', phaseVisual.badgeClass)} variant="outline">
                      {phaseVisual.headline}
                    </Badge>
                  )}
                  {chatSessionId &&
                    (chatAwaitingUser ? (
                      <Badge className="border-blue-300 bg-blue-50 text-xs text-blue-900" variant="outline">
                        Your turn to reply
                      </Badge>
                    ) : !chatFinished ? (
                      <Badge variant="outline" className="text-xs text-muted-foreground">
                        In progress
                      </Badge>
                    ) : null)}
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>{phaseVisual.headline}</span>
                    <span>{phaseVisual.progress}%</span>
                  </div>
                  <Progress value={phaseVisual.progress} className="h-1.5 bg-slate-200/80" />
                  <p className="text-xs leading-relaxed text-muted-foreground">{phaseVisual.sub}</p>
                </div>
              </CardHeader>

              <CardContent className="flex flex-1 flex-col gap-0 p-0">
                {chatError && (
                  <div className="border-b border-red-100 bg-red-50/80 px-4 py-3">
                    <Alert variant="destructive" className="border-red-200 bg-white/90">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle className="text-sm">Request failed</AlertTitle>
                      <AlertDescription className="whitespace-pre-wrap font-mono text-xs">{chatError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <ScrollArea className="min-h-[280px] flex-1">
                  <div className="space-y-4 p-4">
                    {chatMessages.length === 0 && !chatSessionId && (
                      <div className="rounded-xl border border-dashed border-purple-200/80 bg-gradient-to-br from-purple-50/50 to-indigo-50/30 p-8 text-center">
                        <Sparkles className="mx-auto mb-3 h-12 w-12 text-purple-300" />
                        <p className="text-sm font-medium text-slate-800">No session yet</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Click <strong>New chat</strong> to begin a conversation and describe the ABAP you need.
                        </p>
                      </div>
                    )}

                    {chatMessages.filter((m) => !isHiddenChatMessage(m)).map((msg, i) => (
                      <ChatMessageRow
                        key={`chat-msg-${i}`}
                        role={msg.role}
                        content={displayChatMessageContent(msg.content, {
                          role: msg.role,
                          generatedCode: msg.generatedCode ?? chatGeneratedCode,
                          phase: msg.phase ?? chatPhase,
                        })}
                      />
                    ))}

                    {(chatSending || chatGeneratingActive) && (
                      <div className="flex gap-3 pt-1">
                        <div
                          className={cn(
                            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br shadow-md ring-2 ring-purple-200 ring-offset-2 ring-offset-background',
                            phaseVisual.ring
                          )}
                        >
                          <Brain className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 rounded-xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 p-3 shadow-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-purple-900">Assistant</span>
                            <TypingDots />
                          </div>
                          <p className="text-xs font-medium text-purple-700">
                            {chatGeneratingActive ? 'Generating ABAP…' : 'Processing your message…'}
                          </p>
                        </div>
                      </div>
                    )}

                    <div ref={chatEndRef} />
                  </div>
                </ScrollArea>

                <div className="border-t border-purple-100 bg-gradient-to-r from-purple-50 to-pink-50/90 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <Textarea
                      placeholder={
                        chatFinished
                          ? 'Session finished — start a new chat to continue.'
                          : chatSessionId
                            ? 'Your reply… (Enter to send, Shift+Enter for newline)'
                            : 'Start a session with New chat…'
                      }
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      disabled={!chatSessionId || chatFinished || chatSending || chatStarting}
                      className="min-h-[92px] flex-1 resize-none border-purple-200/60 bg-white/95 text-sm shadow-inner focus-visible:ring-purple-300"
                      data-testid="chat-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (canSendChat) sendChatMessage();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={sendChatMessage}
                      disabled={!canSendChat}
                      size="lg"
                      className="shrink-0 bg-gradient-to-r from-blue-600 to-indigo-600 shadow-md hover:from-blue-700 hover:to-indigo-700 sm:h-[92px] sm:px-6"
                      data-testid="chat-send"
                    >
                      {chatSending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    </Button>
                  </div>
                  {chatFinished && (
                    <p className="mt-3 flex items-center gap-2 text-xs text-emerald-800">
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
                      Session complete — use <strong>New chat</strong> for another ABAP program.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Artifacts column */}
            <div className="flex flex-col gap-4 xl:col-span-5">
              {showPrdArtifact && (
                <PrdArtifactCard
                  key={chatSessionId}
                  sessionId={chatSessionId}
                  phase={chatPhase}
                  prdAvailable={prdAvailable}
                  prdChecking={prdChecking}
                />
              )}
              <Card className="border-slate-200/90 shadow-md xl:sticky xl:top-4 xl:self-start">
                <div className="rounded-t-xl border-b border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="rounded-md border border-slate-200 bg-white p-1.5 shadow-sm">
                      <Code2 className="h-4 w-4 text-slate-700" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Generated ABAP</CardTitle>
                      <p className="text-xs text-slate-600">Your generated SAP ABAP program</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-4">
                  {showAbapPanel ? (
                    <>
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        {chatFinished ? (
                          <Badge className="border-emerald-300 bg-emerald-50 text-emerald-900">Ready</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            In progress
                          </Badge>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-slate-200"
                          onClick={copyGeneratedCode}
                          disabled={!chatGeneratedCode}
                        >
                          {copied ? (
                            <>
                              <Check className="mr-2 h-4 w-4 text-emerald-600" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="mr-2 h-4 w-4" />
                              Copy
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="max-h-[min(420px,55vh)] overflow-auto rounded-lg border border-slate-800 bg-slate-950 p-4 font-mono text-xs leading-relaxed text-slate-100 shadow-inner ring-1 ring-slate-700/50">
                        <pre className="whitespace-pre-wrap break-words">{chatGeneratedCode || '—'}</pre>
                      </div>
                    </>
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 py-12 text-center">
                      <Code2 className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                      <p className="text-sm text-muted-foreground">No ABAP output yet</p>
                      <p className="mt-1 px-4 text-xs text-muted-foreground">
                        After you approve the requirements and generation completes, your program appears here.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
</div>
          </div>
        </TabsContent>

        <TabsContent value="tickets" className="mt-6 focus-visible:outline-none">
          {tickets.length === 0 ? (
            <Card className="overflow-hidden border-slate-200/90 shadow-md">
              <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-indigo-50/30 px-6 py-4">
                <h2 className="font-semibold text-slate-900">Ticket-based code</h2>
                <p className="text-xs text-muted-foreground">Same entry pattern as a ticket with no agent output yet.</p>
              </div>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Code className="mb-4 h-14 w-14 text-slate-300" />
                <p className="mb-1 text-center font-medium text-slate-700">No code generations yet</p>
                <p className="mb-6 max-w-md text-center text-sm text-muted-foreground">
                  Execute agent workflows on a ticket, then open the editor from here.
                </p>
                <Button onClick={() => navigate('/tickets')} className="bg-gradient-to-r from-purple-600 to-indigo-600 shadow-md">
                  Go to Tickets
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-5">
              {tickets.map((ticket) => {
                const codes = codeGenerations[ticket.id] || [];
                const activeCode = codes.find((c) => c.is_active) || codes[0];
                return (
                  <Card
                    key={ticket.id}
                    className="overflow-hidden border-slate-200/90 shadow-md transition-all hover:shadow-lg"
                    data-testid={`code-card-${ticket.id}`}
                  >
                    <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-indigo-50/25 pb-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className="font-mono text-xs">
                              {ticket.ticket_number}
                            </Badge>
                            {activeCode && (
                              <>
                                <Badge variant="outline" className="text-xs">
                                  {activeCode.language}
                                </Badge>
                                <Badge className="bg-indigo-600 text-xs text-white hover:bg-indigo-600">v{activeCode.version}</Badge>
                              </>
                            )}
                          </div>
                          <CardTitle className="text-xl text-slate-900">{ticket.title}</CardTitle>
                          {activeCode && <CardDescription className="mt-2">{activeCode.description}</CardDescription>}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 rounded-full hover:bg-white/80"
                          onClick={() => navigate(`/vibe-studio/${ticket.ticket_number}`)}
                          data-testid={`open-code-btn-${ticket.id}`}
                        >
                          <ExternalLink className="h-5 w-5" />
                        </Button>
                      </div>
                    </CardHeader>
                    {activeCode && (
                      <CardContent className="pt-5">
                        <div className="rounded-xl border border-slate-800 bg-slate-950 p-4 font-mono text-sm text-slate-50 shadow-inner ring-1 ring-slate-800/80">
                          <pre className="whitespace-pre-wrap break-words">
                            {extractCodeFromBackticks(activeCode.code).slice(0, 500)}
                            {extractCodeFromBackticks(activeCode.code).length > 500 ? '…' : ''}
                          </pre>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
                          <div className="text-xs text-muted-foreground">
                            {codes.length} version{codes.length !== 1 ? 's' : ''} ·{' '}
                            {new Date(activeCode.updated_at).toLocaleString()}
                          </div>
                          <Button
                            onClick={() => navigate(`/vibe-studio/${ticket.ticket_number}`)}
                            data-testid={`edit-code-btn-${ticket.id}`}
                            className="bg-gradient-to-r from-slate-800 to-slate-900 shadow-sm hover:from-slate-900 hover:to-black"
                          >
                            <Code className="mr-2 h-4 w-4" /> Open in Editor
                          </Button>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
